import { createElement, useEffect, useRef } from 'react';

import { Button } from '@bookora/ui';
import { useQueryClient } from '@tanstack/react-query';

import type { GuidanceStateResponse } from '@/api/generated/model';
import { queryKeys } from '@/api/query-keys';
import { useSessionStore } from '@/auth/token-store';
import { getNotificationApi } from '@/lib/feedback';

import { useRecordGuidance } from '../api';
import { useStepContext } from '../context';
import { useTourFeatures } from '../features';
import { helpNoteFor } from '../help-notes';
import { mayOfferHelpNow } from '../offer-context';
import { waitForQuiet } from '../quiet';
import { RUNTIME_COPY } from '../runtime-copy';
import { noteHelpShown, writeHelpBackoff } from '../storage';
import { HELP_MAX_WAIT_MS, HELP_PENDING_MAX_MS, HELP_QUIET_MS, HELP_RETRY_MS } from '../timing';
import { useTourStore } from '../tour-store';
import { TOURS } from '../tours/registry';
import { installStruggleCollectors } from './collectors';
import { resolveHelpTarget } from './resolve';
import type { StruggleTrip } from './score';
import { useStruggleStore } from './store';

const NOTICE_KEY = 'bk-guidance-help';

/**
 * Struggle detection v1 (critic C6), mounted once by `GuidanceRoot`. Renders
 * nothing: it installs the collectors, and when a threshold trips it offers
 * help — at most once per context a day, through the ONE budget:
 *
 *  · on a page whose tour chip is on screen: the chip's quiet offer, in help
 *    words («کمک می‌خواهید؟»), at the step the person was stuck on when that
 *    is known — recorded as `OFFERED` with source `struggle`;
 *  · elsewhere: a small, non-recording notice with «کمک برای همین صفحه»
 *    (the page's help notes, and support from there);
 *  · an inline form's abandons wait for that form's next open
 *    (`InlineTourSlot`), not here.
 *
 * Nothing while impersonating, while the clinic is billing read-only, while
 * guidance is busy or a dialog is on top (a trip is retried for two minutes
 * until the dialog closes), and never mid-entry: it waits for a four-second
 * pause with the caret out of a half-filled field.
 */
export function StruggleWatch(): null {
  const queryClient = useQueryClient();
  const record = useRecordGuidance();
  const ctx = useStepContext();
  const features = useTourFeatures();
  const trip = useStruggleStore((s) => s.trip);
  const ctxRef = useRef(ctx);
  ctxRef.current = ctx;
  const featuresRef = useRef(features);
  featuresRef.current = features;

  useEffect(() => installStruggleCollectors(), []);

  useEffect(() => {
    if (!trip) return;
    const controller = new AbortController();
    const clinicId = useSessionStore.getState().activeClinicId;
    const fresh = (): GuidanceStateResponse | undefined =>
      queryClient.getQueryData<GuidanceStateResponse>(queryKeys.guidance.state(clinicId));
    const drop = (): void => {
      if (useStruggleStore.getState().trip === trip) useStruggleStore.setState({ trip: null });
    };

    void (async () => {
      // A dialog may be on top when the trip happens (a failed save in a
      // drawer): wait for it to close, but not for ever.
      while (!mayOfferHelpNow(fresh(), trip.context)) {
        if (controller.signal.aborted) return;
        if (Date.now() - trip.at > HELP_PENDING_MAX_MS) {
          drop();
          return;
        }
        await new Promise((resolve) => setTimeout(resolve, HELP_RETRY_MS));
      }
      const quiet = await waitForQuiet(HELP_QUIET_MS, HELP_MAX_WAIT_MS, controller.signal, {
        notEditing: true,
      });
      if (controller.signal.aborted) return;
      if (!quiet || !mayOfferHelpNow(fresh(), trip.context)) {
        drop();
        return;
      }
      drop();
      offerHelp(trip);
    })();
    return () => controller.abort();

    function offerHelp(current: StruggleTrip): void {
      const store = useTourStore.getState();
      const target = resolveHelpTarget({
        trip: current,
        pathname: store.pathname,
        search: store.search,
        ctx: ctxRef.current,
        features: featuresRef.current,
        pageReady: store.pageReady,
      });
      // No tour here and nowhere to put the notice (the toast host is not
      // mounted yet): nothing is shown, so nothing is spent.
      const notifications = target.kind === 'notes' ? getNotificationApi() : null;
      if (target.kind === 'notes' && !notifications) return;

      const session = useSessionStore.getState();
      const now = new Date();
      noteHelpShown(now);
      writeHelpBackoff(session.user?.id ?? null, session.activeClinicId, current.context, {
        shownAt: now.toISOString(),
      });

      if (target.kind === 'tour') {
        store.setOffer(target.tour, {
          context: current.context,
          ...(target.startAtStepId ? { startAtStepId: target.startAtStepId } : {}),
        });
        record({
          type: 'OFFERED',
          kind: 'TOUR',
          key: target.tour,
          version: TOURS[target.tour].version,
          source: 'struggle',
        });
        return;
      }

      // No tour to offer here: a quiet notice, recording nothing (there is no
      // item to record it against), that leads to the page's notes.
      if (!notifications) return;
      const section = helpNoteFor(target.section) ? target.section : '';
      notifications.info({
        key: NOTICE_KEY,
        message: RUNTIME_COPY.help.title,
        description: RUNTIME_COPY.help.supportBody,
        duration: 12,
        actions: createElement(
          Button,
          {
            size: 'small',
            onClick: () => {
              notifications.destroy(NOTICE_KEY);
              useTourStore.getState().setHelpNotes(section);
            },
          },
          RUNTIME_COPY.help.notes,
        ),
      });
    }
  }, [trip, queryClient, record]);

  return null;
}
